import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Copy, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'cb_prospect';
const DISMISS_KEY = 'cb_prospect_banner_dismissed_v1';
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

/**
 * Shows a one-time welcome banner on the landing page for prospects who previously
 * submitted the Contact Us form. Reads their stored inquiry_id, verifies it against
 * the backend, and offers a small promo code. Dismissible for the session.
 */
export const ReturningProspectBanner = ({ onViewPackages }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const raw = (() => { try { return localStorage.getItem(STORAGE_KEY); } catch { return null; } })();
    if (!raw) return;
    if (sessionStorage.getItem(DISMISS_KEY)) return;
    let parsed;
    try { parsed = JSON.parse(raw); } catch { return; }
    if (!parsed?.inquiry_id) return;
    if (parsed.saved_at && Date.now() - parsed.saved_at > MAX_AGE_MS) {
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/public/prospect/${parsed.inquiry_id}`);
        if (res.data?.first_name) {
          setData(res.data);
          // Small delay so it doesn't fight with hero animations
          setTimeout(() => setVisible(true), 600);
        }
      } catch {
        // Stale/archived inquiry — forget it
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      }
    })();
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
  };

  const copyPromo = async () => {
    if (!data?.promo_code) return;
    try {
      await navigator.clipboard.writeText(data.promo_code);
      toast.success(`Promo code ${data.promo_code} copied`);
    } catch {
      toast.info(data.promo_code);
    }
  };

  return (
    <AnimatePresence>
      {visible && data && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="sticky top-16 z-40 px-3"
          data-testid="returning-prospect-banner"
        >
          <div className="max-w-5xl mx-auto mt-3 rounded-2xl border border-[#FFD166] bg-gradient-to-r from-[#FFF4E6] via-[#FFE8EC] to-[#FFF4E6] shadow-sm overflow-hidden">
            <div className="px-4 sm:px-5 py-3 flex items-center gap-3 sm:gap-4">
              <div className="hidden sm:flex w-9 h-9 rounded-full bg-white items-center justify-center shadow-sm flex-shrink-0">
                <Sparkles className="w-4 h-4 text-[#FF3B6B]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm sm:text-base font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  Welcome back, {data.first_name}!
                </p>
                <p className="text-xs sm:text-sm text-[#6B6B7A] truncate">
                  Here's <strong className="text-[#FF3B6B]">{data.promo_percent}% off</strong> your first party — use code{' '}
                  <button
                    onClick={copyPromo}
                    className="inline-flex items-center gap-1 font-mono font-semibold text-[#1E1E24] underline decoration-dotted underline-offset-2 hover:text-[#FF3B6B] transition-colors"
                    data-testid="prospect-banner-copy-code"
                  >
                    {data.promo_code}
                    <Copy className="w-3 h-3" />
                  </button>
                </p>
              </div>
              <button
                onClick={() => navigate(`/book?promo=${encodeURIComponent(data.promo_code)}`)}
                className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-white bg-[#FF3B6B] hover:bg-[#E62E58] transition-colors rounded-full px-4 py-1.5"
                data-testid="prospect-banner-cta"
              >
                Claim {data.promo_percent}% off <ArrowRight className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={dismiss}
                aria-label="Dismiss"
                className="flex-shrink-0 text-[#6B6B7A] hover:text-[#1E1E24] transition-colors rounded-full p-1 hover:bg-white"
                data-testid="prospect-banner-dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReturningProspectBanner;
